import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@shared/api/supabaseClient';
import { logger } from '@shared/lib/logger';
import { userApi } from '@shared/api/users';
import type { AuthState, UserProfile } from '@shared/types/auth';

interface AuthContextValue extends AuthState {
  error: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const INITIAL_STATE: AuthContextValue = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  signInWithPassword: async () => {
    throw new Error('AuthProvider not registered');
  },
  signInWithMagicLink: async () => {
    throw new Error('AuthProvider not registered');
  },
  signOut: async () => {
    throw new Error('AuthProvider not registered');
  },
  refreshSession: async () => {
    throw new Error('AuthProvider not registered');
  },
};

const AUTH_CACHE_KEY = 'jema_auth_state';
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const profileCache = new Map<string, { value: UserProfile; expiresAt: number }>();

type CachedAuthPayload = {
  user: UserProfile | null;
  isAuthenticated: boolean;
  timestamp: number;
};

const loadCachedState = (): AuthState | null => {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedAuthPayload;
    if (!parsed.timestamp) return null;
    const isFresh = Date.now() - parsed.timestamp < PROFILE_CACHE_TTL;
    if (!isFresh) return null;
    return {
      user: parsed.user,
      isAuthenticated: parsed.isAuthenticated,
      isLoading: true,
    };
  } catch (error) {
    logger.warn('Failed to restore cached auth state', { error });
    return null;
  }
};

const cacheState = (state: AuthState) => {
  try {
    const payload: CachedAuthPayload = {
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      timestamp: Date.now(),
    };
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    logger.warn('Failed to cache auth state', { error });
  }
};

const buildUserProfile = (
  session: Session,
  apiProfile: Awaited<ReturnType<typeof userApi.getProfile>>,
): UserProfile => {
  const { user } = session;
  const email = user.email || 'user@example.com';
  const firstName = (user.user_metadata?.first_name as string | undefined) || 'User';
  const lastName = (user.user_metadata?.last_name as string | undefined) || 'Name';

  return {
    id: apiProfile.id,
    role: apiProfile.role,
    as_patient_id: apiProfile.as_patient_id,
    as_doctor_id: apiProfile.as_doctor_id,
    email,
    first_name: firstName,
    last_name: lastName,
    onboarding_complete: apiProfile.onboarding_complete || false,
  };
};

const fetchProfile = async (session: Session): Promise<UserProfile> => {
  const cacheEntry = profileCache.get(session.user.id);
  if (cacheEntry && cacheEntry.expiresAt > Date.now()) {
    return cacheEntry.value;
  }

  const response = await userApi.getProfile();
  const profile = buildUserProfile(session, response);
  profileCache.set(session.user.id, {
    value: profile,
    expiresAt: Date.now() + PROFILE_CACHE_TTL,
  });
  return profile;
};

const AuthContext = createContext<AuthContextValue>(INITIAL_STATE);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const cachedState = useMemo(() => loadCachedState(), []);
  const [state, setState] = useState<AuthState>({
    user: cachedState?.user ?? null,
    isAuthenticated: cachedState?.isAuthenticated ?? false,
    isLoading: true,
  });
  const [error, setError] = useState<string | null>(null);

  const setAuthState = useCallback((next: AuthState, nextError: string | null = null) => {
    setState(next);
    setError(nextError);
    cacheState(next);
  }, []);

  const syncSession = useCallback(
    async (session: Session | null) => {
      if (!session?.user) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false });
        profileCache.clear();
        return;
      }

      setAuthState({ ...state, isLoading: true });

      try {
        const profile = await fetchProfile(session);
        setAuthState({ user: profile, isAuthenticated: true, isLoading: false });
        logger.debug('Auth session synced', { userId: profile.id, role: profile.role });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error('Failed to fetch user profile', { error: message });
        
        // Debug: Log the actual error to help with debugging
        console.log('Auth error details:', { 
          message, 
          error: err, 
          errorType: err?.constructor?.name,
          status: (err as any)?.status,
          code: (err as any)?.code,
          name: (err as any)?.name
        });
        
        // Check if user is suspended - check both message content and status code
        const errorStatus = (err as any)?.status;
        const errorCode = (err as any)?.code;
        const errorName = (err as any)?.name;
        
        const isSuspended = 
          message.includes('suspended') || 
          message.includes('SUSPENDED') || 
          message.includes('Your account has been suspended') ||
          errorStatus === 403 ||
          errorCode === 'FORBIDDEN' ||
          errorName === 'ApiClientError' && errorStatus === 403;
        
        // Additional debug logging for suspension detection
        console.log('Suspension check:', {
          message,
          errorStatus,
          errorCode,
          errorName,
          isSuspended,
          errorType: err?.constructor?.name,
          fullError: err
        });
        
        if (isSuspended) {
          const suspensionMessage = 'Your account has been suspended. Please contact support for assistance.';
          logger.warn('User account suspended', { userId: session.user.id, message });
          setAuthState({ user: null, isAuthenticated: false, isLoading: false }, suspensionMessage);
          // Redirect to error page with suspension message
          window.location.href = `/auth/error?message=${encodeURIComponent(suspensionMessage)}&code=SUSPENDED`;
          return;
        } else {
          setAuthState({ user: null, isAuthenticated: false, isLoading: false }, message);
        }
        
        await supabase.auth.signOut();
      }
    },
    [setAuthState],
  );

  useEffect(() => {
    let isMounted = true;

    const hydrate = async () => {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (!isMounted) return;

      if (sessionError) {
        logger.warn('Initial session fetch failed', { error: sessionError.message });
        setAuthState({ user: null, isAuthenticated: false, isLoading: false }, sessionError.message);
        return;
      }

      await syncSession(data.session);
    };

    hydrate();

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      await syncSession(session);
    });

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, [syncSession, setAuthState]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error: signInError, data } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      logger.warn('Password sign-in failed', { email, error: signInError.message });
      throw signInError;
    }

    await syncSession(data.session);
  }, [syncSession]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    // Use app URL if configured, otherwise fall back to current origin
    // This allows magic links to work in webview apps by using a consistent domain
    const appUrl = import.meta.env.VITE_APP_URL;
    const redirectUrl = appUrl 
      ? `${appUrl}/auth/callback`
      : `${window.location.origin}/auth/callback`;

    const { error: magicLinkError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (magicLinkError) {
      logger.warn('Magic link sign-in failed', { email, error: magicLinkError.message });
      throw magicLinkError;
    }
  }, []);

  const signOut = useCallback(async () => {
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      logger.error('Sign out failed', { error: signOutError.message });
      throw signOutError;
    }
    profileCache.clear();
    setAuthState({ user: null, isAuthenticated: false, isLoading: false });
  }, [setAuthState]);

  const refreshSession = useCallback(async () => {
    const { data, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      logger.error('Session refresh failed', { error: refreshError.message });
      throw refreshError;
    }
    await syncSession(data.session);
  }, [syncSession]);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    error,
    signInWithPassword,
    signInWithMagicLink,
    signOut,
    refreshSession,
  }), [state, error, signInWithPassword, signInWithMagicLink, signOut, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
